import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { findDOMRange } from 'slate-react';

import { CurrentWord } from 'libs/editor/plugins/helpers/currentWord';

import { HighlightedSelection } from 'apps/autocomplete/views/highlightedSelection';

@observer
export class AutocompleteDropdown extends React.Component {
  render() {
    const { store } = this.props;
    const { value, autocompleteStore, PickSuggestion, component } = store;
    const { position, selectedItem, matches, visible, input } =
      autocompleteStore;
    const { SelectItem } = autocompleteStore;
    let top = -10000;
    let left = -10000;
    if (value.selection.isFocused && matches && matches.length && position) {
      try {
        const DOMRange = findDOMRange(position);
        const positionRect = DOMRange && DOMRange.getBoundingClientRect();
        const editorRect = component.getBoundingClientRect();
        if (positionRect) {
          // - I just eyeballed these values for chat and issues and comments
          // - backend now returns a maximum of three matching usernames so that
          // simplifies this a bit further
          // - the point of this top calculation is positioning the suggestions
          // popup, I made the changes above so it would appear above the text
          // so it doesn't interfere with editing
          top = matches.length * -52;
          left = positionRect.left - editorRect.left;
        }
      } catch (err) {
        console.error(err);
      }
    }

    return ReactDOM.createPortal(
      <Suggestions top={top} left={left} visible={visible}>
        {matches.map((e, i) => {
          // - eslint forced me to do this!
          // - this is so matches can be either from a connection, or just
          // a regular query
          const { node } = e.node ? e : { node: e };
          const text = node.filename
            ? node.filename
            : node.username || node.slug || `#${e.node.name}`;
          return (
            <Suggestion
              key={i}
              selected={selectedItem === i}
              onMouseEnter={() => {
                SelectItem(i);
              }}
              // eslint-disable-next-line
              onMouseDown={e => {
                e.preventDefault();
                PickSuggestion();
              }}
              handleKeyPress={evt => {
                if (evt.key === 'Enter') {
                  evt.preventDefault();
                  PickSuggestion();
                }
              }}
            >
              <HighlightedSelection
                optionText={text}
                selection={input.substring(1)}
                inserveHighlighting
              />
            </Suggestion>
          );
        })}
      </Suggestions>,
      component,
    );
  }
}

export function Autocomplete(store) {
  return {
    onKeyDown: (e, editor, next) => {
      const { PickSuggestion, autocompleteStore, value } = store;
      const { Discard, SelectItem, selectedItem, visible } = autocompleteStore;
      if (value.selection.isFocused && visible) {
        switch (e.key) {
          case 'ArrowDown':
            SelectItem(selectedItem + 1);
            e.preventDefault();
            return false;
          case 'ArrowUp':
            SelectItem(selectedItem - 1);
            e.preventDefault();
            return false;
          case 'Enter':
            PickSuggestion();
            e.preventDefault();
            return false;
          case 'Escape':
            Discard();
            e.preventDefault();
            return false;
          default:
            return next();
        }
      }
      return next();
    },
    onChange: editor => {
      const { value } = editor;
      const { autocompleteStore } = store;
      if (value.selection.isFocused && value.focusBlock) {
        const currentWord = CurrentWord(value);
        autocompleteStore.checkInput(currentWord);
      }
    },
  };
}

AutocompleteDropdown.propTypes = {
  store: PropTypes.object,
};

const Suggestions = styled.ul`
  display: ${props => (props.visible ? 'block' : 'none')};
  position: absolute;
  z-index: 1000;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  background-color: ${props => props.theme.color.wfWhite};
  transition: opacity 0.75s;
  box-shadow: rgba(67, 90, 111, 0.416) 0px 0px 1px,
    rgba(67, 90, 111, 0.3) 0px 5px 8px -4px;
  overflow: hidden;
`;

const Suggestion = styled.li`
  display: block;
  padding: 8px 12px 8px 6px;
  margin: 10px;
  min-width: 140px;
  color: ${props => props.theme.color.wfGrey};
  transition: padding 200ms ease-in-out;
  ${props =>
    props.selected &&
    `
    background-color: ${props.theme.color.primary};
    cursor: pointer;
    color: ${props.theme.color.wfWhite};
    padding: 8px 6px 8px 12px;
  `}
`;
